import React, { useEffect, useState } from 'react';
import SelectroV2 from '@aha-app/selectro-v2';
import { useController } from '@aha-app/mvc';
import WhiteboardController from '../controllers/WhiteboardController';
import { type WorkflowKind } from '../shared-types';

export interface FeatureTypeSelectProps {
  releaseId: RecordId;
  currentId?: RecordId;
  select: (kind?: WorkflowKind) => void;
}

const useFeatureTypes = (releaseId: RecordId): WorkflowKind[] => {
  const controller = useController(WhiteboardController);
  const [records, setRecords] = useState<WorkflowKind[]>([]);
  useEffect(() => {
    if (!releaseId) {
      setRecords([]);
      return;
    }
    if (controller.recordManager) {
      controller.recordManager.getWorkflowKinds(releaseId).then(setRecords);
    }
  }, [releaseId]);
  return records;
};

const FeatureTypeSelect: React.FC<FeatureTypeSelectProps> = ({
  releaseId,
  currentId,
  select,
}) => {
  const records = useFeatureTypes(releaseId);
  const currentItem = records.find(({ id }) => id === currentId);
  const currentName = currentItem?.name;
  const options = records.map(kind => ({ text: kind.name, value: kind.id }));
  const selectedElements: typeof options = [];
  if (currentId) {
    selectedElements.push({ text: currentName || '', value: currentId });
  } else if (options.length > 0) {
    selectedElements.push(options[0]);
  }

  // We select the first item by default, so callback `select` on first render
  useEffect(() => {
    if (selectedElements.length === 0) {
      return;
    }
    select(records.find(kind => kind.id === selectedElements[0].value));
  }, [releaseId, records]);

  return (
    <SelectroV2
      disabled={!releaseId}
      containerFullWidth={false}
      items={options}
      selected={selectedElements}
      callbacks={{
        select: ({ selected: [{ value }] }) =>
          select?.(records.find(kind => kind.id === value)),
      }}
    />
  );
};

export default FeatureTypeSelect;
